import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { beginApiCall, completeApiCall } from './api-call-history';
import { captureHttpBody, captureHttpHeaders } from './http-diagnostic-data';
import { notifyHttpCall, type HttpCallStatus, beginHttpDetailCapture } from './http-observers';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Suppress the request body from the device-local admin API history. */
    pointcraftDebugSensitiveRequest?: boolean;
  }

  export interface InternalAxiosRequestConfig {
    pointcraftDebugSensitiveRequest?: boolean;
    pointcraftDebugCallId?: string | null;
    pointcraftRequestStartedAt?: number;
    pointcraftDiagnosticCapture?: (() => boolean) | null;
  }
}

const INSTANCE_MARKER = Symbol.for('pointcraft.api-call-history.axios-instance');
const STATIC_MARKER = Symbol.for('pointcraft.api-call-history.axios-static');

type MarkedAxiosInstance = AxiosInstance & { [INSTANCE_MARKER]?: boolean };
type MarkedAxiosStatic = typeof axios & { [STATIC_MARKER]?: boolean };

function getErrorDetails(error: unknown): {
  errorCode: string | null;
  errorMessage: string | null;
} {
  const axiosError = error as AxiosError;
  return {
    errorCode: typeof axiosError?.code === 'string' ? axiosError.code : null,
    errorMessage: typeof axiosError?.message === 'string' ? axiosError.message : null,
  };
}

function elapsedMs(config: InternalAxiosRequestConfig | undefined): number | null {
  const startedAt = config?.pointcraftRequestStartedAt;
  return typeof startedAt === 'number' ? Date.now() - startedAt : null;
}

function diagnosticDetails(
  config: InternalAxiosRequestConfig | undefined,
  response?: AxiosResponse
) {
  try {
    if (!config?.pointcraftDiagnosticCapture?.()) return {};
    return {
      requestHeaders: captureHttpHeaders(config.headers),
      requestBody: captureHttpBody(config.data, String(config.headers?.['Content-Type'] ?? '')),
      ...(response
        ? {
            responseHeaders: captureHttpHeaders(response.headers),
            responseBody: captureHttpBody(
              response.data,
              String(response.headers?.['content-type'] ?? '')
            ),
          }
        : {}),
    };
  } catch {
    return {};
  }
}

function installTracking(instance: AxiosInstance): AxiosInstance {
  const markedInstance = instance as MarkedAxiosInstance;
  if (markedInstance[INSTANCE_MARKER]) return instance;
  if (!instance.interceptors?.request || !instance.interceptors?.response) return instance;
  markedInstance[INSTANCE_MARKER] = true;

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const requestUrl = axios.getUri(config);
    config.pointcraftRequestStartedAt = Date.now();
    config.pointcraftDiagnosticCapture = beginHttpDetailCapture(requestUrl);
    config.pointcraftDebugCallId = beginApiCall(
      {
        method: config.method,
        url: requestUrl,
        body: config.data,
        sensitiveRequest: config.pointcraftDebugSensitiveRequest,
      },
      config.baseURL
    );
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const status: HttpCallStatus =
        response.status >= 200 && response.status < 300 ? 'SUCCEEDED' : 'HTTP_ERROR';
      completeApiCall(response.config.pointcraftDebugCallId, {
        status,
        httpStatus: response.status,
      });
      notifyHttpCall({
        transport: 'axios',
        method: (response.config.method ?? 'GET').toUpperCase(),
        url: axios.getUri(response.config),
        status,
        httpStatus: response.status,
        durationMs: elapsedMs(response.config),
        errorCode: null,
        errorMessage: null,
        sensitive: response.config.pointcraftDebugSensitiveRequest === true,
        ...diagnosticDetails(response.config, response),
      });
      return response;
    },
    (error: unknown) => {
      const axiosError = error as AxiosError;
      const config = axiosError.config as InternalAxiosRequestConfig | undefined;
      const canceled = axiosError.code === 'ERR_CANCELED' || axiosError.name === 'CanceledError';
      const details = getErrorDetails(error);
      let status: HttpCallStatus = 'NETWORK_ERROR';
      if (canceled) status = 'CANCELED';
      else if (axiosError.response) status = 'HTTP_ERROR';
      completeApiCall(config?.pointcraftDebugCallId, {
        status,
        httpStatus: axiosError.response?.status ?? null,
        ...details,
      });
      notifyHttpCall({
        transport: 'axios',
        method: (config?.method ?? 'GET').toUpperCase(),
        url: config ? axios.getUri(config) : '',
        status,
        httpStatus: axiosError.response?.status ?? null,
        durationMs: elapsedMs(config),
        ...details,
        sensitive: config?.pointcraftDebugSensitiveRequest === true,
        ...diagnosticDetails(config, axiosError.response),
      });
      return Promise.reject(error);
    }
  );

  return instance;
}

const trackedAxios = axios as MarkedAxiosStatic;
if (!trackedAxios[STATIC_MARKER]) {
  installTracking(axios);
  if (typeof axios.create === 'function') {
    const originalCreate = axios.create.bind(axios);
    axios.create = (config) => installTracking(originalCreate(config));
  }
  trackedAxios[STATIC_MARKER] = true;
}

export default axios;
export type { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
